/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, useRef } from "react";
import {
	motion,
	useAnimation,
	useInView,
	useReducedMotion,
} from "motion/react";
import { useTranslation } from "react-i18next";
import { useGsapScrollParallax } from "@/hooks/useGsapScrollParallax";
import Card3D, { type Card3DVariant } from "../card3D";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Static (non-translatable) product card data. Titles/descriptions are resolved
// from the active locale at render time via `titleKey` / `descKey`.
const productCards = [
	{
		image: "/images/finalProducts/DOUBLE SAFETY WITH LEKAGE DETETCTION WIRE.png",
		href: "/products/shell-tube-heat-exchangers",
		titleKey: "productsSection.items.shellTube.title",
		descKey: "productsSection.items.shellTube.description",
	},
	{
		image: "/images/finalProducts/UNIT COOLER (OFAF).png",
		href: "/products/air-cooled-heat-exchangers",
		titleKey: "productsSection.items.airCooled.title",
		descKey: "productsSection.items.airCooled.description",
	},
	{
		image: "/images/finalProducts/WATER COOLED AIR COOLER.png",
		href: "/products/water-cooled-air-coolers",
		titleKey: "productsSection.items.waterCooled.title",
		descKey: "productsSection.items.waterCooled.description",
	},
	{
		image: "/images/products/doble-removebg-preview.png",
		href: "/products/double-pipe-heat-exchangers",
		titleKey: "productsSection.items.doublePipe.title",
		descKey: "productsSection.items.doublePipe.description",
	},
	{
		image: "/images/products/Recuperato-removebg-preview.png",
		href: "/products/recuperators",
		titleKey: "productsSection.items.recuperators.title",
		descKey: "productsSection.items.recuperators.description",
	},
	{
		image: "/images/finalProducts/DRY COOLER.png",
		href: "/products/dry-coolers",
		titleKey: "productsSection.items.dryCoolers.title",
		descKey: "productsSection.items.dryCoolers.description",
		// Landscape artwork: the shared square photo box renders it ~1.5x the
		// card's width, so it gets a tighter box that stays inside the card.
		imageSize: "h-64 w-64 scale-100 sm:h-80 sm:w-80",
		// The unit sits ~4% left of centre inside its own canvas, so the box is
		// nudged right by the same amount to make it read as centred.
		imagePosition: "left-1/2 top-[60%] -translate-x-[46%] -translate-y-1/2",
	},
	{
		image: "/images/finalProducts/ONAN.png",
		href: "/products/onan",
		titleKey: "productsSection.items.onan.title",
		descKey: "productsSection.items.onan.description",
	},
];

const isMobile = window.innerWidth < 768;

const transformStyles = [
	"rotate(5deg) translate(-150px)",
	"rotate(0deg) translate(-70px)",
	"rotate(-5deg)",
	"rotate(5deg) translate(70px)",
	"rotate(-5deg) translate(150px)",
];

const buttonVariants = {
	initial: { scale: 1 },
	hover: {
		scale: 1.04,
		transition: {
			type: "spring",
			stiffness: 340,
			damping: 18,
		},
	},
	tap: { scale: 0.98 },
};

export default function ProductsOverview() {
	const { t } = useTranslation();
	const controls = useAnimation();
	const ref = useRef<HTMLElement | null>(null);
	const shouldReduceMotion = useReducedMotion();
	const isInView = useInView(ref, { once: true, amount: 0.25 });
	useGsapScrollParallax(ref, { y: -90, opacity: 0.9, scale: 0.98 });

	// Resolve localized titles/descriptions for the product cards.
	const localizedProducts = productCards.map((card) => ({
		image: card.image,
		href: card.href,
		title: t(card.titleKey),
		description: t(card.descKey),
		imageSize: card.imageSize,
		imagePosition: card.imagePosition,
	}));

	useEffect(() => {
		if (isInView) {
			controls.start("visible");
		}
	}, [controls, isInView]);

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: shouldReduceMotion ? 0 : 0.12,
				delayChildren: 0.15,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 18 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const },
		},
	};

	return (
		<motion.section
			ref={ref}
			initial="hidden"
			animate={controls}
			variants={containerVariants}
			className="site-section bg-backgroundA">
			<div className="site-container px-4 sm:px-6 md:px-8 lg:px-12 mx-auto max-w-7xl w-full">
				<motion.div
					variants={itemVariants}
					className="mx-auto mb-8 sm:mb-10 md:mb-12 lg:mb-14 max-w-4xl text-center px-2 sm:px-0">
					<span className="section-kicker justify-center text-xs sm:text-sm">
						<span className="section-kicker-line" />
						{t("productsSection.kicker")}
						<span className="section-kicker-line" />
					</span>

					<h2 className="section-title text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-3 sm:mt-4 md:mt-5">
						{t("productsSection.title")}
					</h2>
					<p className="section-subtitle text-xs sm:text-sm md:text-base lg:text-lg mt-3 sm:mt-4">
						<span className="block font-medium text-foreground/90 text-sm sm:text-base mb-2">
							{t("productsSection.subtitle")}
						</span>
						{t("productsSection.description")}
					</p>
				</motion.div>
				{/* {!isMobile ?
					<motion.div
						variants={itemVariants}
						className="overflow-x-hidden">
						<BounceCards
							className="custom-bounceCards"
							images={images}
							containerWidth={1200}
							containerHeight={250}
							animationDelay={1}
							animationStagger={0.08}
							easeType="elastic.out(1, 0.5)"
							transformStyles={transformStyles}
							enableHover
						/>
					</motion.div>
				:	<div className="h-54 w-full flex items-center justify-center ">
						<div className="h-52 w-52 flex items-center justify-center self-center">
							<Stack
								randomRotation={false}
								sensitivity={200}
								sendToBackOnClick={true}
								cards={images.map((src, i) => (
									<img
										key={i}
										src={src}
										alt={`card-${i + 1}`}
										style={{
											width: "100%",
											height: "100%",
											objectFit: "cover",
										}}
									/>
								))}
								autoplay={false}
								autoplayDelay={3000}
								pauseOnHover={false}
							/>
						</div>
					</div>
				} */}
				{/* <motion.div
					variants={itemVariants}
					whileHover="hover"
					whileTap="tap"
					initial="initial"
					animate="initial"
					className="mt-6 sm:mt-8 md:mt-10 lg:mt-12 flex justify-center px-4 sm:px-0">
					<Link
						to="/products"
						className="w-full sm:w-auto">
						<motion.button
							variants={buttonVariants}
							className="premium-button group w-full sm:w-auto px-6 sm:px-8 md:px-10 py-3 sm:py-4 text-sm sm:text-base">
							Explore Products
							<svg
								className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M14 5l7 7m0 0l-7 7m7-7H3"
								/>
							</svg>
						</motion.button>
					</Link>
				</motion.div> */}
				<IndustrySectionProduct applications={localizedProducts} />{" "}
			</div>
		</motion.section>
	);
}

type CardItem = {
	image: string;
	title: string;
	description: string;
	href?: string;
	/** Optional Tailwind size override for the card photo (see `Card3D`). */
	imageSize?: string;
	/** Optional Tailwind placement override for the card photo. */
	imagePosition?: string;
};

// Cycle accent colors so adjacent cards never share a tint.
const VARIANT_CYCLE: Card3DVariant[] = [
	"amber",
	"indigo",
	"teal",
	"rose",
	"emerald",
];

export const IndustrySectionProduct: React.FC<{
	applications: CardItem[];
	title?: string;
	description?: string;
}> = ({ applications }) => {
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) return;

		const anim = gsap.fromTo(
			ref.current,
			{ opacity: 0, y: 60 },
			{
				opacity: 1,
				y: 0,
				duration: 0.8,
				ease: "power3.out",
			},
		);

		const trigger = ScrollTrigger.create({
			trigger: ref.current,
			start: "top 85%",
			animation: anim,
		});

		return () => trigger.kill();
	}, []);

	return (
		<div
			ref={ref}
			className="mb-20">
			<div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-x-2 gap-y-4 px-4 sm:gap-y-6 sm:px-6">
				{applications.map((item, i) => (
					<motion.div
						key={`${item.title}-${i}`}
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, margin: "-60px" }}
						transition={{
							delay: i * 0.08,
							duration: 0.55,
							ease: [0.22, 1, 0.36, 1],
						}}
						className="flex justify-center">
						<Card3D
							title={item.title}
							description={item.description}
							image={item.image}
							imageSizeClassName={item.imageSize}
							imagePositionClassName={item.imagePosition}
							href={item.href}
							variant={VARIANT_CYCLE[i % VARIANT_CYCLE.length]}
							eyebrow="POSITRON"
							subEyebrow="POSITRON"
							ctaLabel={t("productsSection.explore")}
						/>
					</motion.div>
				))}
			</div>
		</div>
	);
};
